import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Checkbox from './checkbox'

const internationalize = (...args) => !window.I18n ? '' : window.I18n.t(...args)

const switchExpression = (lookupTable) => {
  const processedLookupTable = Object.keys(lookupTable).reduce((newTable, oldKey) => {
    oldKey.split('|').forEach(key => newTable[key] = lookupTable[oldKey])
    return newTable
  }, {})
  const search = (key) => processedLookupTable[key]

  return search
}

const parseIndex = (index) => {
  if (index && (index[0] === '+' || index[0] === 'p' || index[0] === 'P')) { return -1 * parseFloat(index.slice(1)) }
  return parseFloat(index)
}

const partitionByGroups = (total, groupCount) => {
  if (groupCount === 0) { return [] }

  const base = Array(groupCount).fill(Math.floor(total / groupCount))
  const excess = Array(total % groupCount).fill(1)

  return base.map((v, i) => v + (excess[i] || 0))
}

const partitionByMax = (total, max) => {
  return partitionByGroups(total, max === 0 ? 0 : Math.ceil(total / max))
}

export const categoryItemBuilder = (category) => (name, genderAnswers, numberOfGolfers) => ({
  name: name,
  index_up_to: '',
  [`golfers_per_${category}`]: numberOfGolfers,
  genders: Object.fromEntries(genderAnswers.map(gender => [ gender, gender === genderAnswers[0] ])),
  age_range: {
    from: '',
    to: '',
  },
})

function DivisionsFlightsTable({
  category,
  golferLabel,
  withIndexUpTo,
  withNumberOfGolfers,
  withGenders,
  oneGender,
  withAgeRange,
  genderAnswers,
  rowClass,
  setCount,
  items,
  fixedItems,
  setItemData,
  indexes,
  setIndex,
  golfersDivisionsFlights,
  setError,
  errorCheckingDependencies,
}) {
  const categoryLabel = internationalize(`dashboard_plus.divisions_flights.${category}`)
  const handicapIndex = rowClass === 'combined_index_usga_index'

  const headers = [ {
    name: 'name',
    label: internationalize('dashboard_plus.divisions_flights.name', { category: categoryLabel }),
    enabled: true,
  }, {
    name: 'index_up_to',
    label: handicapIndex ? internationalize('divisions.inprogress_division.index_up_to', gon.whs_term_formats) : internationalize('divisions.inprogress_division.playing_handicap_up_to', gon.whs_term_formats),
    enabled: withIndexUpTo,
  }, {
    name: `golfers_per_${category}`,
    label: internationalize('dashboard_plus.divisions_flights.number_of_golfers', { ...window.participantFormats, category: categoryLabel }),
    enabled: withNumberOfGolfers,
  }, {
    name: 'genders',
    label: internationalize('divisions.index.age_gender.genders'),
    enabled: withGenders,
  }, {
    name: 'age_range',
    label: internationalize('divisions.index.age_gender.age_range'),
    enabled: withAgeRange,
  } ]
  const enabledHeaders = headers.filter(x => x.enabled)

  const buildItem = categoryItemBuilder(category)
  const newItem = (name, numberOfGolfers = 0) => {
    return buildItem(name, genderAnswers, numberOfGolfers)
  }

  const addItem = () => {
    if (categoryLabel === 'Division') {
      if (fixedItems.type !== 'disabled' ) {
        const selected = golfersDivisionsFlights.selected
        setCount(parseInt(golfersDivisionsFlights.choices[selected].value + 1))
      }
    } else {
      if (fixedItems.type !== 'disabled' ) {
        const selected = golfersDivisionsFlights.selected
        setCount(parseInt(golfersDivisionsFlights.choices[selected].value + 1))
      }
    }
    setItemData([ items.length ], newItem(`${categoryLabel} ${items.length + fixedItems.startIndex}`))
  }

  const resetItems = () => {
    if (fixedItems.type !== 'disabled' ) { 
      setCount(0)
      const localIndexes = JSON.parse(JSON.stringify(indexes))
      items.forEach((item) => {
        if (Array.isArray(item.no_golfers)) {
          localIndexes.push(...item.no_golfers)
        }
      })
      setIndex(localIndexes)
    }
    setItemData([], [])
  }
  const totalGolfers = items.map(item => parseInt(item[`golfers_per_${category}`]) || 0).reduce((a, b) => a + b, 0)
  const selectedGolfers = items.map(item => item.no_golfers ? item.no_golfers.length : 0).reduce((a, b) => a + b, 0)

  useEffect(() => {
    if (fixedItems.type === 'fixed') {
      while (items.length < fixedItems.value) {
        items.push(newItem(`${categoryLabel} ${items.length + fixedItems.startIndex}`))
      }
      items = items.slice(0, fixedItems.value)
      setItemData([], items)

    } else if (fixedItems.type === 'max_golfers') {
      const partitions = partitionByMax(fixedItems.total, fixedItems.value)
      if (fixedItems.editing && partitions.length === items.length) {
        const updatedItems = items.map((item, index) => {
          return newItem(item.name, partitions[index] || 0)
        })

        setItemData([], updatedItems)
      } else {
        const newItems = partitions.map((count, index) => newItem(`${categoryLabel} ${index + fixedItems.startIndex}`, count))
        if (items.length !== 0 || newItems.length !== 0) { setItemData([], newItems) }
      }
    }
  }, (fixedItems.dependencies || [ fixedItems.value ]))

  useEffect(() => {
    const errorKey = `${DivisionsFlightsTable.name}_${category}`

    let error = false
    items.find(item => {
      enabledHeaders.find(header => {
        const element = item[header.name]
        error = (switchExpression({
          'name': () => element.trim().length > 0 ? false : internationalize('dashboard_plus.divisions_flights.error_messages.name_empty', { category: categoryLabel }),
          'index_up_to': () => {
            if (/[pP+-]?\d+-\d+/.test(element) || /\.{2,}/.test(element) || /[^pP+\d.]+/.test(element) || /\.$/.test(element)) {
              return internationalize('dashboard_plus.divisions_flights.error_messages.invalid_handicap_index')
            } else {
              return false
            }
          },
          [`golfers_per_${category}`]: () => parseInt(element) > 0 ? false : internationalize('dashboard_plus.divisions_flights.error_messages.positive_number', { element: header.label }),
          'age_range': () => {
            const from = parseInt(element.from)
            const to = parseInt(element.to)
            return isNaN(from) || isNaN(to) || from <= to ? false : internationalize('dashboard_plus.divisions_flights.error_messages.invalid_age_range')
          },
        })(header.name) || (() => false))()

        return error
      })
      return error
    })

    if (fixedItems.type === 'max_golfers') {
      const totalGolfers = items.map(item => parseInt(item[`golfers_per_${category}`]) || 0).reduce((a, b) => a + b, 0)
      if (totalGolfers > fixedItems.total) {
        error = internationalize('assets.application.flights.total_greater', { count: fixedItems.total, participant_plural: window.participantFormats.participant_plural })
      }
    }
    if (items.length === 0) {
      error = internationalize('dashboard_plus.divisions_flights.error_messages.empty_table', { category: categoryLabel })
    }
    setError(errorKey, error)

    return () => { setError(errorKey, undefined) }
  }, errorCheckingDependencies)

  const lastEditedRef = useRef({updated: false, index: null, value: null })
  useEffect(() => {
    const {updated, index: changedIndex, value: inputValue } = lastEditedRef.current
    if (category !== 'division' || updated || !withIndexUpTo) { return }

    let localIndexes = JSON.parse(JSON.stringify(indexes))

    items.forEach((item, index) => {
      let itemNoGolfers
      const itemIndexValue = parseIndex(item.index_up_to)

      if (index === changedIndex) {
        const parsedInputValue = parseIndex(inputValue)
        itemNoGolfers = localIndexes.filter(i => parseIndex(i.index) <= parsedInputValue)
      } else {
        itemNoGolfers = localIndexes.filter(i => parseIndex(i.index) <= itemIndexValue)
      }

      localIndexes = localIndexes.filter(
        indexObj => !itemNoGolfers.some(golfer => golfer.index === indexObj.index),
      )

      setItemData([ index, 'no_golfers' ], itemNoGolfers)
    })

    lastEditedRef.current = {updated: true, index: null, value: null }
  }, [ items ])

  return (
    <div>
      <table className={`${category}s_table table table-striped`}>
        <thead>
          <tr className='header'>
            {
              enabledHeaders.map((header, index) =>
                <th key={`${header.name}-${index}`}>{ header.label }</th>)
            }
          </tr>
        </thead>
        <tbody>
          {
            items.length === 0 ?
              <tr>
                <td className='center' colSpan={headers.length}>{ internationalize(`dashboard_plus.divisions_flights.no_${category}s`) }</td>
              </tr>
              :
              <>
                { items.map((item, index) => {
                    return (
                      <tr key={`${category}_${index}`}>
                        {
                          /* eslint-disable */
                          enabledHeaders.map(header =>
                            <td className={`${category}_${header.name} ${rowClass}`} key={`${category}_${index}_${header.name}`}>
                              {
                                switchExpression({
                                  name: () => (
                                    <input className='form-control' type='text' value={item[header.name]} onChange={e => setItemData([ index, header.name ], e.target.value)} />
                                  ),
                                  [`index_up_to|golfers_per_${category}`]: () => (
                                    <div className='flex'>
                                      <input
                                        className='form-control'
                                        type='text'
                                        value={item[header.name]}
                                        onChange={e => {
                                          lastEditedRef.current = {updated: false, index, value: e.target.value }
                                          setItemData([index, header.name], e.target.value)
                                        }}
                                        onBlur={e => {
                                          if (header.name === 'index_up_to') { return }
                                          const v = parseInt(e.target.value) || ''
                                          setItemData([ index, header.name ], v === '' ? v : Math.max(v, 0))
                                        }}
                                      />
                                      { withIndexUpTo && category === 'division' && fixedItems.dependencies[1] === 'combined_index_usga_index' &&
                                        <span>
                                          {`${item.no_golfers?.length ?? 0} ${(item.no_golfers?.length === 1) ? golferLabel.one : golferLabel.other}`}
                                        </span>
                                      }
                                    </div>
                                  ),
                                  genders: () => (
                                    <div>
                                      {
                                        Object.keys(item.genders).map(gender =>
                                          <Checkbox
                                            key={ `gender_${gender}_${category}_${index}` }
                                            label={gender}
                                            selected={item.genders[gender]}
                                            className={`${genderAnswers.length <= 1 ? 'disabled' : ''}`}
                                            setOption={(v) => {
                                              if (genderAnswers.length <= 1) { return }

                                              setItemData([ index, header.name, gender ], v)
                                              if (oneGender && genderAnswers.length > 1) {
                                                const otherGender = gender === 'M' ? 'F' : 'M'
                                                setItemData([ index, header.name, otherGender ], !v)
                                              }
                                            }}
                                          />
                                        )
                                      }
                                    </div>
                                  ),
                                  age_range: () => (
                                    <div>
                                      <input className='form-control' type='text' value={item[header.name].from} onChange={e => setItemData([ index, header.name, 'from' ], e.target.value)} />
                                      -
                                      <input className='form-control' type='text' value={item[header.name].to} onChange={e => setItemData([ index, header.name, 'to' ], e.target.value)} />
                                    </div>
                                  ),
                                })(header.name)()
                              }
                            </td>)
                          /* eslint-enable */
                        }
                      </tr>
                    )
                })}
                { ((fixedItems.type === 'fixed' && withIndexUpTo && handicapIndex) ||
                  fixedItems.type === 'max_golfers') &&
                  <tr className='total_golfers_row'>
                    <td>
                      <span className='add-margin-left-10'>
                        { fixedItems.type === 'max_golfers' 
                          ? internationalize('divisions.inprogress_division.total_golfers', window.participantFormats)
                          : internationalize('divisions.index.by_multiple_criteria.selected_golfers') }
                      </span>
                    </td>
                    <td>
                      <span className='add-margin-left-15'>
                        { fixedItems.type === 'max_golfers'
                          ? totalGolfers
                          : `${selectedGolfers} / ${fixedItems.total}` }
                      </span>
                    </td>
                    { enabledHeaders.length - 2 > 0 &&
                      <td colSpan={enabledHeaders.length - 2}/>
                    }
                  </tr>
                }
              </>
          }
        </tbody>
      </table>
      <div className='divisions-flights-action-buttons'>
        <a
          className={ `action-button add-${category}-button` }
          href='javascript: void(0);'
          onClick={addItem}>
          + { internationalize('dashboard_plus.divisions_flights.add', { 
                  category: categoryLabel === 'Division' && fixedItems.type !== 'disabled' && golfersDivisionsFlights?.selected !== 'number_of_divisions' ? 
                            'Golfers' : 
                            categoryLabel })} 
        </a>
        <a
          className={ `action-button reset-${category}-button` }
          href='javascript: void(0);'
          onClick={resetItems}>
          { internationalize('dashboard_plus.divisions_flights.reset', { category: categoryLabel }) }
        </a>
      </div>
    </div>
  )
}

DivisionsFlightsTable.propTypes = {
  category: PropTypes.string,
  golferLabel: PropTypes.object,
  withIndexUpTo: PropTypes.bool,
  withNumberOfGolfers: PropTypes.bool,
  withGenders: PropTypes.bool,
  withAgeRange: PropTypes.bool,
  genderAnswers: PropTypes.array,
  oneGender: PropTypes.bool,
  setCount: PropTypes.func,
  golfersDivisionsFlights: PropTypes.object,
  rowClass: PropTypes.string,
  indexes: PropTypes.array,
  items: PropTypes.array,
  fixedItems: PropTypes.shape({
    type: PropTypes.oneOf([ 'disabled', 'max_golfers', 'fixed' ]),
    total: PropTypes.number,
    startIndex: PropTypes.number,
    value: PropTypes.number,
    dependencies: PropTypes.array,
    editing: PropTypes.bool,
  }),
  setItemData: PropTypes.func,
  setIndex: PropTypes.func,
  setError: PropTypes.func,
  errorCheckingDependencies: PropTypes.array,
}

DivisionsFlightsTable.defaultProps = {
  fixedItems: {
    type: 'disabled',
  },
}

export default DivisionsFlightsTable
